import React, { useState, useEffect } from 'react';
import { login } from '../services/authService';
import { getApiUrl } from '../utils/apiUrl';

const initialValues = {
  email: '',
  password: '',
};

const isLocalNetwork = () => {
  const h = window.location.hostname;
  return h.startsWith('192.168.') || h.startsWith('10.') || h.startsWith('172.') || h === 'localhost' || h === '127.0.0.1';
};

const AuthForm = ({ onAuthSuccess, sessionExpiredMsg, clearSessionExpiredMsg }) => {
  const [formValues, setFormValues] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('login'); // 'login', 'forgot', 'reset'
  const [resetToken, setResetToken] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Check URL for reset token on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset-token');
    if (token) {
      setResetToken(token);
      setView('reset');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    clearSessionExpiredMsg?.();

    try {
      const payload = {
        email: formValues.email,
        password: formValues.password,
        platform: 'web',
      };

      const result = await login(payload);

      onAuthSuccess(result);
      setFormValues(initialValues);
    } catch (err) {
      setError(err.message || 'Noe gikk galt.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setForgotMessage('');

    try {
      const res = await fetch(`${getApiUrl()}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      setForgotMessage(data.message || 'Forespørsel sendt.');
    } catch (err) {
      setForgotMessage('Kunne ikke sende forespørsel. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');

    if (newPassword.length < 8) {
      setResetMessage('Passord må være minst 8 tegn.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetMessage('Passordene stemmer ikke overens.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/auth/reset-password/${resetToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setResetSuccess(true);
        setResetMessage(data.message || 'Passordet er oppdatert!');
      } else {
        setResetMessage(data.message || 'Kunne ikke tilbakestille passord.');
      }
    } catch (err) {
      setResetMessage('Noe gikk galt. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  // Forgot password view
  if (view === 'forgot') {
    return (
      <div className="auth-card">
        <div className="auth-panel">
          <span className="auth-badge">Produksjonsstyring</span>
          <h1>Glemt passord?</h1>
          <p>Skriv inn e-posten din, så sender vi en lenke for å tilbakestille passordet.</p>
        </div>
        <form onSubmit={handleForgotPassword} className="auth-form">
          <div className="form-header">
            <h2>Tilbakestill passord</h2>
          </div>
          <div className="input-group">
            <label htmlFor="forgot-email">E-postadresse</label>
            <input
              id="forgot-email"
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              autoComplete="email"
              required
              autoFocus
            />
          </div>
          {forgotMessage && (
            <p style={{ color: '#4ade80', fontSize: '0.9rem', margin: '0.5rem 0' }}>{forgotMessage}</p>
          )}
          <button type="submit" className="submit-btn full-width" disabled={loading}>
            {loading ? 'Sender …' : 'Send tilbakestillingslenke'}
          </button>
          <button
            type="button"
            onClick={() => { setView('login'); setForgotMessage(''); }}
            style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', marginTop: '1rem', fontSize: '0.9rem' }}
          >
            ← Tilbake til innlogging
          </button>
        </form>
      </div>
    );
  }

  // Reset password view (from email link)
  if (view === 'reset') {
    return (
      <div className="auth-card">
        <div className="auth-panel">
          <span className="auth-badge">Produksjonsstyring</span>
          <h1>Nytt passord</h1>
          <p>Velg et nytt passord for kontoen din.</p>
        </div>
        <form onSubmit={handleResetPassword} className="auth-form">
          <div className="form-header">
            <h2>Sett nytt passord</h2>
          </div>
          {!resetSuccess ? (
            <>
              <div className="input-group">
                <label htmlFor="new-password">Nytt passord</label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                  autoFocus
                />
              </div>
              <div className="input-group">
                <label htmlFor="confirm-password">Bekreft passord</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </div>
              {resetMessage && (
                <p className="form-error" role="alert">{resetMessage}</p>
              )}
              <button type="submit" className="submit-btn full-width" disabled={loading}>
                {loading ? 'Oppdaterer …' : 'Oppdater passord'}
              </button>
            </>
          ) : (
            <>
              <p style={{ color: '#4ade80', fontSize: '0.95rem', margin: '1rem 0' }}>{resetMessage}</p>
              <button
                type="button"
                onClick={() => { setView('login'); setResetSuccess(false); }}
                className="submit-btn full-width"
              >
                Gå til innlogging
              </button>
            </>
          )}
        </form>
      </div>
    );
  }

  const isExternal = window.location.protocol === 'https:' && !isLocalNetwork();

  return (
    <div className="auth-card">
      <div className="auth-panel">
        <span className="auth-badge">Produksjonsstyring</span>
        <h1>Velkommen tilbake</h1>
        <p>Logg inn for å få tilgang til dashbordene og styre modulene i sanntid.</p>
      </div>

      {isExternal ? (
        <div className="auth-form" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔒</div>
          <h2 style={{ marginBottom: '0.75rem', color: '#e5e9f0' }}>Ekstern tilgang deaktivert</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.9rem', lineHeight: '1.5' }}>
            Innlogging er kun tilgjengelig fra lokalt nettverk eller via appen.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-header">
            <h2>Logg inn</h2>
          </div>

          {sessionExpiredMsg && (
            <div style={{ background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.35)', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.1rem' }}>⚠️</span>
              <span style={{ color: '#fbbf24', fontSize: '0.85rem', fontWeight: 500 }}>{sessionExpiredMsg}</span>
            </div>
          )}

          <div className="input-group">
            <label htmlFor="email">E-postadresse</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formValues.email}
              onChange={handleChange}
              autoComplete="email"
              required
            />
          </div>

          <div className="input-group">
            <label htmlFor="password">Passord</label>
            <input
              id="password"
              name="password"
              type="password"
              value={formValues.password}
              onChange={handleChange}
              autoComplete="current-password"
              required
            />
          </div>

          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="submit-btn full-width" disabled={loading}>
            {loading ? 'Logger deg inn …' : 'Logg inn'}
          </button>

          <button
            type="button"
            onClick={() => setView('forgot')}
            style={{
              background: 'none', border: 'none', color: '#94a3b8',
              cursor: 'pointer', marginTop: '0.75rem', fontSize: '0.85rem',
              textDecoration: 'underline',
            }}
          >
            Glemt passord?
          </button>
        </form>
      )}
    </div>
  );
};

export default AuthForm;
